import { FEATURES } from './features';

/**
 * Enum for link types.
 * @readonly
 * @enum {string}
 */
const LINK_TYPE = {
  WIDGET: 'widget',
  CONTENT: 'content',
};

/**
 * Enum for image types of `LINK_META.image`
 * @readonly
 * @enum {string}
 */
const LINK_META_CATEGORY = {
  DEFAULT: 'default',
  RECOMMEND: 'recommend',
};

const WidgetTypes = {
  LINK: 0,
  SUPPORT: 1,
  SUBSCRIPTION: 2,
  EMAIL_CAPTURE: 3,
  HEADER: 4,
  CONTENT_LINK: 5,
};

const LinkCategories = {
  GENERIC: 0,
  SPOTIFY: 1,
  SOUNDCLOUD: 2,
  YOUTUBE: 3,
  TWITTER: 4,
  CALENDLY: 5,
  VIMEO: 6,
  TYPE_FORM: 7,
  ACAST: 8,
  AMAZON_BOOKS: 9,
  GOOGLE_MAPS: 10,
};

const WidgetIcons = {
  [WidgetTypes.LINK]: require('@/assets/icons/optimized/dashboard/linksTab/link.svg'),
  [WidgetTypes.SUPPORT]: require('@/assets/icons/optimized/dashboard/linksTab/support-me.svg'),
  [WidgetTypes.SUBSCRIPTION]: require('@/assets/icons/optimized/dashboard/linksTab/subscription.svg'),
  [WidgetTypes.EMAIL_CAPTURE]: require('@/assets/icons/optimized/dashboard/linksTab/email-capture.svg'),
};

const WIDGET_META = {
  [`${WidgetTypes.HEADER}-`]: {
    title: 'Header',
    description: 'Organise your links into sections',
    featureName: FEATURES.HEADERS,
    icon: {
      [LINK_META_CATEGORY.DEFAULT]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/header.svg'),
      [LINK_META_CATEGORY.RECOMMEND]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/recommendations/header.svg'),
    },
  },
  [`${WidgetTypes.LINK}-${LinkCategories.YOUTUBE}`]: {
    title: 'YouTube',
    description: 'Add a YouTube video',
    featureName: FEATURES.EMBED_YOUTUBE,
    icon: {
      [LINK_META_CATEGORY.DEFAULT]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/youtube.svg'),
      [LINK_META_CATEGORY.RECOMMEND]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/recommendations/youtube.svg'),
    },
  },
  [`${WidgetTypes.LINK}-${LinkCategories.GENERIC}`]: {
    title: {
      [LINK_META_CATEGORY.DEFAULT]: 'Link anywhere',
      [LINK_META_CATEGORY.RECOMMEND]: 'Link',
    },
    description: 'Add a link to any URL',
    featureName: FEATURES.UNLIMITED_LINKS,
    icon: {
      [LINK_META_CATEGORY.DEFAULT]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/link.svg'),
      [LINK_META_CATEGORY.RECOMMEND]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/recommendations/link.svg'),
    },
  },
  [`${WidgetTypes.LINK}-${LinkCategories.SPOTIFY}`]: {
    title: 'Spotify',
    description: 'Add a song, playlist or album from Spotify',
    featureName: FEATURES.EMBED_SPOTIFY,
    icon: {
      [LINK_META_CATEGORY.DEFAULT]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/spotify.svg'),
      [LINK_META_CATEGORY.RECOMMEND]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/recommendations/spotify.svg'),
    },
  },
  [`${WidgetTypes.SUPPORT}-`]: {
    title: 'Tip jar',
    description: 'Receive tips or crowdfund your next project',
    featureName: FEATURES.COLLECT_TIPS,
    icon: {
      [LINK_META_CATEGORY.DEFAULT]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/growMonetise/support.svg'),
      [LINK_META_CATEGORY.RECOMMEND]: require('@/assets/icons/optimized/dashboard/addSomethingPopup/recommendations/support.svg'),
    },
  },
  [`${WidgetTypes.LINK}-${LinkCategories.SOUNDCLOUD}`]: {
    title: 'SoundCloud',
    description: 'Add a SoundCloud track',
    featureName: FEATURES.EMBED_SOUNDCLOUD,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/soundcloud.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.TWITTER}`]: {
    title: 'X (Twitter)',
    description: 'Embed your latest tweet or Twitter feed',
    featureName: FEATURES.EMBED_TWITTER,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/twitter.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.CALENDLY}`]: {
    title: 'Calendly',
    description: 'Embed your Calendly page',
    featureName: FEATURES.EMBED_CALENDLY,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/calendly.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.VIMEO}`]: {
    title: 'Vimeo',
    description: 'Embed your Vimeo video',
    featureName: FEATURES.EMBED_VIMEO,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/vimeo.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.TYPE_FORM}`]: {
    title: 'Typeform',
    description: 'Embed your Typeform form',
    featureName: FEATURES.EMBED_TYPE_FORM,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/typeform.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.ACAST}`]: {
    title: 'Acast',
    description: 'Embed your Acast podcast',
    featureName: FEATURES.EMBED_ACAST,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/acast.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.AMAZON_BOOKS}`]: {
    title: 'Amazon Books',
    description: 'Embed your Amazon book',
    featureName: FEATURES.EMBED_AMAZON_BOOKS,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/amazon_books.svg'),
  },
  [`${WidgetTypes.LINK}-${LinkCategories.GOOGLE_MAPS}`]: {
    title: 'Google maps',
    description: 'Embed your Google map',
    featureName: FEATURES.EMBED_GOOGLE_MAPS,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/createShare/google_maps.svg'),
  },
  [`${WidgetTypes.SUBSCRIPTION}-`]: {
    title: 'Subscription',
    description: 'Start a free or paid subscription to your content',
    featureName: FEATURES.CREATE_AND_SELL_MEMBERSHIP_AND_SUBSCRIPTIONS,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/growMonetise/subscription.svg'),
  },
  [`${WidgetTypes.EMAIL_CAPTURE}-`]: {
    title: 'Email capture',
    description: 'Capture emails, grow your email list',
    featureName: FEATURES.CAPTURE_EMAILS,
    icon: require('@/assets/icons/optimized/dashboard/addSomethingPopup/growMonetise/capture.svg'),
  },
};

export {
  LINK_TYPE,
  LINK_META_CATEGORY,
  WIDGET_META,
  WidgetTypes,
  LinkCategories,
  WidgetIcons,
};
